import React, { useEffect, useState, useCallback, useContext } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import CustomMapMarker from "./components/CustomMapMarker";
import EventMapMarker from "./components/EventMapMarker";
import CategoryMultiSelect from "./components/CategoryMultiSelect";
import ToggleBar from "./components/ToggleBar";
import SearchResult from "./components/SearchResult";
import SearchResultEvent from "./components/SearchResultEvent";
import ContactForm from "./components/ContactForm";
import ContactFormOverlay from "./components/ContactFormOverlay";
import EventForm from "./components/EventForm";
import { MapStylesGreen } from './components/MapStyles'
import { RemoteDataContext } from './providers/RemoteDataProvider';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const SUBMIT_ENDPOINT = process.env.REACT_APP_SUBMIT_ENDPOINT;
const EVENT_FORM_SUBMIT_ENDPOINT = process.env.REACT_APP_EVENT_FORM_SUBMIT_ENDPOINT;

const libraries = ["places"];
const mapContainerStyle = {
  width: "100vw",
  height: "100vh",
  maxWidth: "100%"
};

const defaultCenter = {
  lat: 51.165691,
  lng: 10.451526
};

const defaultZoom = 6;

// only one toggle can be active at a time
// if toggle is active only show these items
const staticToggles = [
  {
    label: "Events",
    value: "showEvents",
    active: false
  },
  {
    label: "Showroom",
    value: "showShowroom",
    active: false
  }
];

const SEARCH_RESULT_TYPE = {
  DEFAULT: "default",
  EVENT: "event",
  SHOWROOM: "showroom"
};

const App = () => {

  const { locations, locationsById, events } = useContext(RemoteDataContext);
  const [filteredLocations, setFilteredLocations] = useState([]);  
  const [toggles] = useState(staticToggles);
  const [selectedToggles, setSelectedToggles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchString, setSearchString] = useState("");
  const [map, setMap] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [showResultEvent, setShowResultEvent] = useState(false); // true: ignore if no location is given => for testing
  const [currentCenter, setCurrentCenter] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [filterCity, setFilterCity] = useState("");  
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchResultType, setSearchResultType] = useState(SEARCH_RESULT_TYPE.DEFAULT);
  const [categories, setCategories] = useState([
    {
      label: "PV & Speicher",
      value: "hasPvSpeicher",
      active: true,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54.2 47"><path fill="currentColor" d="m51.2 8 3-8zM46 21.8h.1l3.4-9.2h-.1z"></path><path fill="currentColor" d="M32.8 21.8H46l3.4-9.2H36.3zM51.2 8l3-8h-13l-3.1 8zM27.9 21.8l3.5-9.2H19.7l-3.5 9.2zM33.2 8l3.1-8H24.6l-3.1 8zM16.6 8l3-8H8.2l-3 8zM14.8 12.6H3.5L0 21.8h11.3zM27.4 40V25.5h-4.6V40c-6.5.4-11.5 2.7-11.5 5.5v1.6h27.5v-1.6c0-2.8-5-5.1-11.5-5.5Z"></path></svg>)
    },
    {
      label: "E-Mobilität",
      value: "hasEMobility",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 76 59"><path fill="none" d="M18.7 10H32V4h-2.9c-5.7 0-10.4 6-10.4 6M36 4v6h16.5S43.3 4 39 4z"></path><path fill="currentColor" d="M59.7 23c0 .3-.1.6-.1 1v1c.6 1.7 2.1 3 4 3s3.4-1.3 3.9-3c0-.3.1-.7.1-1 0-2.2-1.8-4-4-4s-3.4 1.3-3.9 3M19.7 20.2c-1.8 0-3.3 1.3-3.7 2.9 0 .3-.2.7-.2 1v.8c.4 1.8 1.9 3.1 3.8 3.2h.1c1.9 0 3.5-1.4 3.9-3.2v-.8c0-2.2-1.8-4-4-4h-.1Z"></path><path fill="currentColor" d="M74.9 16.9c-.3-.8-1.1-2.2-3.4-3.3-3-1.5-8.5-3.2-12.7-4.2-2.2-1.5-8-4.7-13.7-7.3-2-.9-5.1-2.1-7.9-2.1h-7.5c-7.8 0-17.8 9-17.8 9l-6 1v11.3c.4-.1.9-.2 1.3-.2l-1.3.9v-.7c-3.5.9-6 4-6 7.8V31c0 4.4 3.6 8 8 8.1h54.3c2.2 0 4.1 1.8 4.1 4.1v1.9c0 2.2-1.8 4.1-4.1 4.1H43.6v4h18.6c4.4 0 8-3.6 8-8.1v-1.9c0-4.4-3.6-8-8-8.1H8.1C5.9 35.1 4 33.3 4 31v-1.9C4 26.9 5.8 25 8.1 25h4.7v-.8c0-2 .8-3.7 2.1-5 1.2-1.2 2.9-2 4.8-2 3.8 0 6.9 3.2 6.9 7V26h30.2v-2c0-2.2 1-4.2 2.6-5.5.3-.2.5-.4.8-.6 1.4-.8 3.1-1.2 4.9-.8 3.5.7 5.8 3.9 5.6 7.4v1.4h4.2l1.4-3.9s-1-4.8-1.1-5.1ZM32 10H18.7s4.7-6 10.4-6H32zm4 0V4h3c4.3 0 13.5 6 13.5 6z"></path><path fill="currentColor" d="M37 43h-5v3h-6v3h6v4h-6v3h6v3h5.1c3.3-.4 5.9-2.8 6.7-6v-4c-.8-3.1-3.5-5.6-6.7-6ZM7.3 21c-.4 0-.9.1-1.3.2v.7z"></path></svg>)
    },
    {
      label: "Smart-Meter",
      value: "hasSmartMeter",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" fill="none"><path d="M25.1481 3.27383V24.7321H2.85185V3.27383H25.1481ZM28 0H0V28H28V0Z" fill="currentColor" /><path d="M7.59376 8.519C7.23909 8.981 6.73742 9.212 6.08876 9.212C5.44476 9.212 4.94309 8.981 4.58376 8.519C4.22909 8.06166 4.05176 7.41533 4.05176 6.58C4.05176 5.768 4.22909 5.13566 4.58376 4.683C4.94309 4.23033 5.44476 4.004 6.08876 4.004C6.73276 4.004 7.23209 4.23033 7.58676 4.683C7.94609 5.13566 8.12576 5.768 8.12576 6.58C8.12576 7.41533 7.94842 8.06166 7.59376 8.519ZM5.10176 6.601C5.10176 7.75833 5.43076 8.337 6.08876 8.337C6.74676 8.337 7.07576 7.75833 7.07576 6.601C7.07576 5.453 6.74676 4.879 6.08876 4.879C5.43076 4.879 5.10176 5.453 5.10176 6.601Z" fill="currentColor" /><path d="M15.5049 8.519C15.1502 8.981 14.6485 9.212 13.9999 9.212C13.3559 9.212 12.8542 8.981 12.4949 8.519C12.1402 8.06166 11.9629 7.41533 11.9629 6.58C11.9629 5.768 12.1402 5.13566 12.4949 4.683C12.8542 4.23033 13.3559 4.004 13.9999 4.004C14.6439 4.004 15.1432 4.23033 15.4979 4.683C15.8572 5.13566 16.0369 5.768 16.0369 6.58C16.0369 7.41533 15.8595 8.06166 15.5049 8.519ZM13.0129 6.601C13.0129 7.75833 13.3419 8.337 13.9999 8.337C14.6579 8.337 14.9869 7.75833 14.9869 6.601C14.9869 5.453 14.6579 4.879 13.9999 4.879C13.3419 4.879 13.0129 5.453 13.0129 6.601Z" fill="currentColor" /><path d="M23.4159 8.519C23.0613 8.981 22.5596 9.212 21.9109 9.212C21.2669 9.212 20.7653 8.981 20.4059 8.519C20.0513 8.06166 19.8739 7.41533 19.8739 6.58C19.8739 5.768 20.0513 5.13566 20.4059 4.683C20.7653 4.23033 21.2669 4.004 21.9109 4.004C22.5549 4.004 23.0543 4.23033 23.4089 4.683C23.7683 5.13566 23.9479 5.768 23.9479 6.58C23.9479 7.41533 23.7706 8.06166 23.4159 8.519ZM20.9239 6.601C20.9239 7.75833 21.2529 8.337 21.9109 8.337C22.5689 8.337 22.8979 7.75833 22.8979 6.601C22.8979 5.453 22.5689 4.879 21.9109 4.879C21.2529 4.879 20.9239 5.453 20.9239 6.601Z" fill="currentColor" /><rect x="5" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="7.1" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="12.2002" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="14.3002" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="19.3994" y="21.8" width="4.2" height="1.2" rx="0.6" fill="currentColor" /><circle cx="21.4994" cy="19.1" r="1.5" stroke="currentColor" strokeWidth="1.2" /><rect x="2" y="10" width="24" height="1" fill="currentColor" /><rect x="9.5" y="11" width="9" height="1" transform="rotate(-90 9.5 11)" fill="currentColor" /><rect x="17.5" y="11" width="9" height="1" transform="rotate(-90 17.5 11)" fill="currentColor" /></svg>)
    },
    {
      label: "Wärmepumpe Installation",
      value: "hasWaermepumpeInstall",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 69.2 55.28"><g fill="currentColor"><path d="M64.18 5.4v2.02h-7.43V0c-2.97 1.38-4.93 4.31-4.93 7.81 0 3.21 1.74 6.01 4.33 7.52v22.38c0 2.39 1.97 4.33 4.38 4.33s4.27-1.94 4.27-4.33V15.37a8.69 8.69 0 0 0 4.41-7.57c0-3.62-1.76-6.49-5.03-7.8zM61.7 37.7a1.24 1.24 0 1 1-2.48 0 1.24 1.24 0 0 1 2.48-.01Z"></path><path d="M48.5 22.08v27.7h-43v-27.7zm5.5-5.51H0v38.71h54z"></path><path d="M32.99 26.29h12.16v3.25H32.99zM32.99 32.1h12.16v3.25H32.99zM32.99 37.9h12.16v3.25H32.99zM19.21 24.87c-6.34 0-11.47 5.14-11.47 11.47s5.14 11.47 11.47 11.47 11.47-5.14 11.47-11.47-5.14-11.47-11.47-11.47m8.1 18.94c-1.28 1.39-3.08 1.82-5.21-.14-1.62-1.5-2.23-4.75-2.43-6.22-.14.06-.3.1-.47.1-.21 0-.41-.06-.59-.16-.79 1.36-2.48 3.93-4.44 4.67-2.71 1.03-4.22-.03-4.89-1.8-.68-1.77-.26-3.57 2.45-4.6 1.9-.73 4.76-.01 6.29.48.07-.38.31-.7.65-.87-.71-1.47-1.75-4.11-1.32-6.04.64-2.83 2.36-3.5 4.21-3.08s3.11 1.76 2.48 4.59c-.44 1.96-2.58 3.92-3.84 4.94.13.19.2.42.2.67 0 .13-.03.26-.07.38 1.48.07 4.78.41 6.4 1.91 2.13 1.96 1.85 3.79.57 5.18h.01Z"></path></g></svg>)
    },
    {
      label: "Wärmepumpe Integration",
      value: "hasWaermepumpeIntegration",
      active: false,
      svg: () => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 81.82 60.36"><g fill="currentColor"><path d="M48.5 26.7v27.7h-43V26.7zm5.5-5.51H0V59.9h54z"></path><path d="M32.99 30.9h12.16v3.25H32.99zM32.99 36.71h12.16v3.25H32.99zM32.99 42.51h12.16v3.25H32.99zM19.21 29.49c-6.34 0-11.47 5.14-11.47 11.47s5.14 11.47 11.47 11.47 11.47-5.14 11.47-11.47-5.14-11.47-11.47-11.47m8.1 18.94c-1.28 1.39-3.08 1.82-5.21-.14-1.62-1.5-2.23-4.75-2.43-6.22-.14.06-.3.1-.47.1-.21 0-.41-.06-.59-.16-.79 1.36-2.48 3.93-4.44 4.67-2.71 1.03-4.22-.03-4.89-1.8-.68-1.77-.26-3.57 2.45-4.6 1.9-.73 4.76-.01 6.29.48.07-.38.31-.7.65-.87-.71-1.47-1.75-4.11-1.32-6.04.64-2.83 2.36-3.5 4.21-3.08s3.11 1.76 2.48 4.59c-.44 1.96-2.58 3.92-3.84 4.94.13.19.2.42.2.67 0 .13-.03.26-.07.38 1.48.07 4.78.41 6.4 1.91 2.13 1.96 1.85 3.79.57 5.18h.01ZM55.63 37.95v-3h3.05c2.55 0 4.55 2.78 4.55 6.32v9.18c0 1.83 1.49 3.32 3.32 3.32h9.54v3h-9.54c-3.48 0-6.32-2.83-6.32-6.32v-9.18c0-2-.93-3.32-1.55-3.32z"></path><path d="M75.84 50.2a5.08 5.08 0 1 1-.002 10.162A5.08 5.08 0 0 1 75.84 50.2M55.63 28.99h21.36v3H55.63z"></path><path d="M76.74 35.57a5.08 5.08 0 1 0-.002-10.162 5.08 5.08 0 0 0 .002 10.162M55.63 22.41v3h3.05c2.55 0 4.55-2.78 4.55-6.32V9.91c0-1.83 1.49-3.32 3.32-3.32h9.54v-3h-9.54c-3.48 0-6.32 2.83-6.32 6.32v9.18c0 2-.93 3.32-1.55 3.32z"></path><path d="M75.84 10.16a5.08 5.08 0 1 0-.002-10.162 5.08 5.08 0 0 0 .002 10.162"></path></g></svg>)
    }
  ]);
  const [previousCategories, setPreviousCategories] = useState([]);
  // const [wantsEdsnInfo, setWantsEdsnInfo] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  useEffect(() => {
    // Check if the shadow root is available in the window object
    const shadowRoot = window.e3dcMapShadowRoot;
    if (shadowRoot) {
      const handleSelectEvent = (e) => {
        const event = events[e.detail.id];
        if (!event) {
          console.error('Event not found');
          return;
        }
        setSelectedToggles({ showEvents: true });

        // selectedToogle sets setSelectedEvent(null) wait a little bit before set select event
        setTimeout(() => {
          setSelectedEvent(event);
          setSearchString(`${event.zipcode} ${event.city}`);
          setSearchQuery(`${event.zipcode} ${event.city}`);
          const newCenter = { lat: event.lat, lng: event.lng };
          map.panTo(newCenter);
          map.setZoom(9);
          filterLocations(newCenter);
          filterEvents(newCenter);
        }, 100);

      };
      shadowRoot.addEventListener('e3dcMapSelectEvent', handleSelectEvent);
      window.e3dcMapShadowRootInitialized = true;
      return () => {
        shadowRoot.removeEventListener('e3dcMapSelectEvent', handleSelectEvent);
      };
    }
  }, [events, map]);

  useEffect(() => {
    setFilteredLocations(locations);   
    setCategories(prev =>
      prev.map(cat =>
        cat.value === 'hasPvSpeicher'
          ? { ...cat, active: true }
          : cat
      )
    );

  }, [locations]);

  useEffect(() => {
    setFilteredEvents(events);
  }, [events]);

  // reset map to default settings when searchQuery is empty
  useEffect(() => {
    if (searchQuery.length === 0 && showResult && map) {
      // reset map to default
      map.panTo(defaultCenter);
      map.setZoom(defaultZoom);
      setSelectedLocation(null);
      // @todo: add flag to better control when results should be shown
      // currently "setCurrentCenter(null)" means to skip
      setCurrentCenter(null);
      filterLocations(null, true);

      setSelectedEvent(null);
      filterEvents(null, true);
    }
  }, [searchQuery]);

  // filter location by services and search
  const filterLocations = useCallback((newCenter, skipFilterByDistance = false) => {

    if (newCenter) {
      setCurrentCenter(newCenter);
    }

    var filtered = locations;

    const isActive = (value) => categories.find(cat => cat.value === value)?.active;

    // filter by category
    if (     
      isActive('hasPvSpeicher') ||
      isActive('hasEMobility') ||
      isActive('hasSmartMeter') ||
      isActive('hasWaermepumpeInstall') ||
      isActive('hasWaermepumpeIntegration') ||
      searchResultType === SEARCH_RESULT_TYPE.SHOWROOM
    ) {
      filtered = filtered.filter((location) => {
        var filter = true;   
        // redundant: we disable all filter anyway
        if (searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) {
          filter &= location.hasShowroom;
        } else {
          if (isActive('hasPvSpeicher')) {
            filter &= location.hasPvSpeicher;
          }        
          if (isActive('hasEMobility')) {
            filter &= location.hasEMobility;
          }                
          if (isActive('hasSmartMeter')) {
            filter &= location.hasSmartMeter;
          }        
          if (isActive('hasWaermepumpeInstall')) {
            filter &= location.hasWaermepumpeInstall;
          }        
          if (isActive('hasWaermepumpeIntegration')) {
            filter &= location.hasWaermepumpeIntegration;
          }
          // if (searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) {
          //   filter &= location.hasShowroom;
          // }
        }        
        
        return filter;
      });
    }

    // - calculate distance for locations
    // - sort by distance
    // - get shortest locations
    const center = newCenter ? newCenter : currentCenter;
    if (center && !skipFilterByDistance) {

      // get nearest 20
      var filteredTmp = filtered.map(item => {
        return {
          ...item,
          distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
        };
      })
        .filter(item => parseFloat(item.distance) <= item.radius); // Respect max radius

      if (filteredTmp.length === 0) {
        // when no results where found filter again without radius check
        filtered = filtered.map(item => {
          return {
            ...item,
            distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
          };
        })
      } else {
        filtered = filteredTmp;
      }

      filtered = filtered
        .sort((a, b) => a.distance - b.distance) // Sort by distance in ascending order
        .slice(0, 20); // get 20

      // prioritize alliance
      filtered = filtered.sort((a, b) => {
        if (a.distance < b.distance && a.distance <= 3) return -1; // a ist 1km oder weniger entfernt
        if (b.distance < a.distance && b.distance <= 3) return 1; // b ist 1km oder weniger entfernt
        if (a.partnerType === 2 && b.partnerType !== 2) return -1; // a ist Alliance, kommt zuerst
        if (b.partnerType === 2 && a.partnerType !== 2) return 1;  // b ist Alliance, kommt zuerst
        return 0; // Normal & Premium behalten ihre Reihenfolge
      });

      // get first 10
      filtered = filtered.slice(0, 10);

      setShowResult(true);
    } else {
      setShowResult(false);
    }

    setFilteredLocations(filtered);
  }, [locations, /*selectedCategories,*/ categories, currentCenter, searchResultType]);

  // @todo: set location directly in event
  const filterEvents = useCallback((newCenter, skipFilterByDistance = false) => {

    var filtered = Object.values(events);

    const isActive = (value) => categories.find(cat => cat.value === value)?.active;   

    // filter by category
    if (     
      isActive('hasPvSpeicher') ||
      isActive('hasEMobility') ||
      isActive('hasSmartMeter') ||
      isActive('hasWaermepumpeInstall') ||
      isActive('hasWaermepumpeIntegration')
    ) {
      filtered = filtered.filter((event) => {
        const location = locationsById[event.partnerId];
        if (!location) {
          console.log('event', event);
          console.log('location', location);
          return false;
        }
        var filter = true;        
        if (isActive('hasPvSpeicher')) {
          filter &= location.hasPvSpeicher;
        }        
        if (isActive('hasEMobility')) {
          filter &= location.hasEMobility;
        }        
        if (isActive('hasSmartMeter')) {
          filter &= location.hasSmartMeter;
        }        
        if (isActive('hasWaermepumpeInstall')) {
          filter &= location.hasWaermepumpeInstall;
        }        
        if (isActive('hasWaermepumpeIntegration')) {
          filter &= location.hasWaermepumpeIntegration;
        }
        return filter;
      });
    }

    // - calculate distance for locations
    // - sort by distance
    // - get shortest locations
    const center = newCenter ? newCenter : currentCenter;
    if (center && !skipFilterByDistance) {

      // get nearest 20
      var filteredTmp = filtered.map(item => {
        return {
          ...item,
          distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
        };
      })
        .filter(item => parseFloat(item.distance) <= item.radius); // Respect max radius

      if (filteredTmp.length === 0) {
        // when no results where found filter again without radius check
        filtered = filtered.map(item => {
          return {
            ...item,
            distance: (Math.round(haversineDistance(center.lat, center.lng, item.lat, item.lng) * 10) / 10).toFixed(1)
          };
        })
      } else {
        filtered = filteredTmp;
      }

      filtered = filtered
        .sort((a, b) => a.distance - b.distance) // Sort by distance in ascending order
        .slice(0, 20); // get 20

      // prioritize alliance
      filtered = filtered.sort((a, b) => {
        if (a.distance < b.distance && a.distance <= 3) return -1; // a ist 1km oder weniger entfernt
        if (b.distance < a.distance && b.distance <= 3) return 1; // b ist 1km oder weniger entfernt
        // if (a.partnerType === 2 && b.partnerType !== 2) return -1; // a ist Alliance, kommt zuerst
        // if (b.partnerType === 2 && a.partnerType !== 2) return 1;  // b ist Alliance, kommt zuerst
        return 0; // Normal & Premium behalten ihre Reihenfolge
      });

      // get first 10
      filtered = filtered.slice(0, 10);

      setShowResult(true);
    } else {
      setShowResult(false);
    }

    // console.log('filteredEvents', filtered);
    setFilteredEvents(filtered);
  }, [events, categories, currentCenter]);

  // refilter when active categories change
  useEffect(() => {

    // reset selectedEvent, selectedLocation
    setSelectedEvent(null);
    setSelectedLocation(null);

    filterLocations();
    filterEvents();

  }, [categories, locations]);

  useEffect(() => {

    // reset selectedEvent, selectedLocation
    setSelectedEvent(null); // problem in custom event
    setSelectedLocation(null);

    if (searchResultType == SEARCH_RESULT_TYPE.SHOWROOM) {
      // disable all categories
      setPreviousCategories(categories);
      setCategories(prev =>
        prev.map(cat => ({ ...cat, active: false }))
      );    
    } else {      
      if (previousCategories.length > 0) {
        setCategories(previousCategories);
        setPreviousCategories([]);
      }
    }

    filterLocations();
    // filterEvents();
  }, [searchResultType])

  useEffect(() => {

    // console.log('selectedToggles changed');
    if (selectedToggles.showEvents) {
      // console.log('selectedToggles.showEvents');
      setSearchResultType(SEARCH_RESULT_TYPE.EVENT);
    }
    else if (selectedToggles.showShowroom) {
      // console.log('selectedToggles.showShowroom');      
      setSearchResultType(SEARCH_RESULT_TYPE.SHOWROOM);
    }
    else {
      // console.log('selectedToggles.default');
      setSearchResultType(SEARCH_RESULT_TYPE.DEFAULT);
    }

  }, [selectedToggles]);

  function haversineDistance(lat1, lon1, lat2, lon2) {
    const r = 6371; // km
    const p = Math.PI / 180;
    const a = 0.5 - Math.cos((lat2 - lat1) * p) / 2
      + Math.cos(lat1 * p) * Math.cos(lat2 * p) *
      (1 - Math.cos((lon2 - lon1) * p)) / 2;
    return 2 * r * Math.asin(Math.sqrt(a));
  }

  const handleSearch = () => {
    if (searchQuery) {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address: searchQuery, language: 'de' }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK) {

          const geoLocation = results[0].geometry.location
          const addressComponents = results[0].address_components
          const filteredCity = addressComponents.filter((address_component) => address_component.types.includes('locality'))
          const city = filteredCity.length ? filteredCity[0].long_name : ''
          const name = `${city}`

          setSearchString(name);
          setSearchQuery(name);
          setFilterCity(city);

          // Get the coordinates of the search result
          const newCenter = {
            lat: geoLocation.lat(),
            lng: geoLocation.lng(),
          };

          // Set the map to the new center and zoom in
          map.panTo(newCenter);
          map.setZoom(9);

          // reset form
          setSelectedEvent(null);
          setSelectedLocation(null);

          // show/hide everything out of range
          filterLocations(newCenter);
          filterEvents(newCenter);
        } else {
          console.error('Geocode was not successful for the following reason: ' + status);
        }
      });
    }
  };

  const handleCategoryMultiSelectChange = (category, active) => {    
    setCategories(prevCategories => 
        prevCategories.map(cat => 
            cat.value === category.value 
                ? { ...cat, active }
                : cat
        )
    );
  }

  const handleToggleBarChange = (selected) => {
    // console.log('handleToggleBarChange', selected)
    setSelectedToggles(selected);
  }

  // on enter:
  // - set map marker as active
  // - move to position
  const handleSearchResultItemEnter = (location, index) => {
    map.panTo({ lat: location.lat, lng: location.lng });
    setFilteredLocations((prevLocations) =>
      prevLocations.map((location, i) =>
        i === index
          ? { ...location, active: true }
          : location
      )
    );
  };

  // on leave: set map marker as inactive
  const handleSearchResultItemLeave = (location, index) => {
    setFilteredLocations((prevLocations) =>
      prevLocations.map((location, i) =>
        i === index
          ? { ...location, active: false }
          : location
      )
    );
  }

  // show on leave: set map marker as inactive
  const handleSearchResultItemClicked = (location, index) => {
    if (searchResultType !== SEARCH_RESULT_TYPE.SHOWROOM) {
      setSelectedLocation(location);
    }
  }

  const handleSearchResultEventItemEnter = (event, index) => {
    map.panTo({ lat: event.lat, lng: event.lng });
    setFilteredEvents((prevEvents) =>
      prevEvents.map((event, i) =>
        i === index
          ? { ...event, active: true }
          : event
      )
    );
  };

  // on leave: set map marker as inactive
  const handleSearchResultEventItemLeave = (event, index) => {
    setFilteredEvents((prevEvents) =>
      prevEvents.map((event, i) =>
        i === index
          ? { ...event, active: false }
          : event
      )
    );
  }

  // show on leave: set map marker as inactive
  const handleSearchResultEventItemClicked = (event, index) => {
    setSelectedEvent(event);
  }

  const handleContactFormClose = () => {
    setSelectedLocation(null);
  }

  const handleMarkerClick = (location) => {

    setSearchString(`${location.zipcode} ${location.city}`); // Set the city name
    setSearchQuery(`${location.zipcode} ${location.city}`);

    // Set the map to the new center and zoom in
    const newCenter = { lat: location.lat, lng: location.lng };
    map.panTo(newCenter);
    map.setZoom(9);

    // Show/hide locations/events based on proximity
    filterLocations(newCenter);
    filterEvents(newCenter);

    // Set location
    setSelectedLocation(location);
  }

  const handleEventMarkerClick = (event) => {

    // Set event
    setSelectedEvent(event);
    // console.log('event', event);

    setSearchString(`${event.zipcode} ${event.city}`); // Set the city name
    setSearchQuery(`${event.zipcode} ${event.city}`);

    // Set the map to the new center and zoom in
    const newCenter = { lat: event.lat, lng: event.lng };
    map.panTo(newCenter);
    map.setZoom(9);

    // Show/hide locations/events based on proximity
    filterLocations(newCenter);
    filterEvents(newCenter);
  }

  const handleEventFormClose = () => {
    setSelectedEvent(null);
  }

  const handleSearchReset = () => {
    setSearchQuery('');
  }

  const handleGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Get the coordinates of the search result
          const { latitude, longitude } = position.coords;
          const geocoder = new window.google.maps.Geocoder();
          const latLng = { lat: latitude, lng: longitude };

          // Reverse geocode to get address components, including the city
          geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
              const addressComponents = results[0].address_components;
              const filteredCity = addressComponents.filter((component) =>
                component.types.includes('locality')
              );
              const city = filteredCity.length ? filteredCity[0].long_name : '';
              const name = `${city}`;
              setSearchString(name); // Set the city name
              setSearchQuery(name);
              setFilterCity(city);
              // Set the map to the new center and zoom in
              const newCenter = { lat: latitude, lng: longitude };
              map.panTo(newCenter);
              map.setZoom(9);
              // reset form
              setSelectedEvent(null);
              setSelectedLocation(null);
              // Show/hide locations based on proximity
              filterLocations(newCenter);
              filterEvents(newCenter);
            } else {
              console.error('Geocode was not successful for the following reason: ' + status);
            }
          });
        }
      );
    }
  }

  return (
    <div className='e3dcp map-container'>
      <div className='map-bg-gradient'></div>
      <div className='map-overlay-container'>
        {/* {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) && */}
        {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT) &&         
          <ContactForm          
            location={selectedLocation}
            action={SUBMIT_ENDPOINT}
            onClose={handleContactFormClose}
            filterCity={filterCity}            
            categories={categories}                    
          />         
        }
        {searchResultType === SEARCH_RESULT_TYPE.EVENT &&
          <EventForm
            event={selectedEvent}
            action={EVENT_FORM_SUBMIT_ENDPOINT}
            onClose={handleEventFormClose}
            filterCity={filterCity}            
            categories={categories}
          />
        }
        <div className="row">
          <div className='col-12'>
            {/* @todo: component for search input */}
            <div className='map-search p-3 d-flex'>
              <div
                onClick={handleGeolocation}
                className='map-search-geolocation'>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><path stroke="none" d="M0 0h24v24H0z"></path><path d="M9 12a3 3 0 1 0 6 0 3 3 0 1 0-6 0"></path><path d="M4 12a8 8 0 1 0 16 0 8 8 0 1 0-16 0M12 2v2M12 20v2M20 12h2M2 12h2"></path></svg>
              </div>
              <input
                className='map-search-input'
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchQuery.length > 2) {
                    handleSearch();
                  }
                }}
                placeholder="PLZ oder Ort"
              />
              <button className='map-search-button' onClick={handleSearch}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="map-search-button-icon"><path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0 7 7 0 0 0-14 0m18 11-6-6"></path></svg>
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className='col-12'>
            {/* Checkboxes */}
            <CategoryMultiSelect
              categories={categories}
              onChange={handleCategoryMultiSelectChange}
              disabled={searchResultType === SEARCH_RESULT_TYPE.SHOWROOM}
            />
          </div>
        </div>
        <div className="row">
          <div className='col-12'>
            {/* Toggles */}
            <ToggleBar
              toggles={toggles}
              selectedToggles={selectedToggles}
              onChange={handleToggleBarChange}
            />
          </div>
        </div>
        <div className="row">
          <div className='col-12'>
            {/* Search Result */}
            {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) &&
              <SearchResult
                showResult={showResult}
                locations={filteredLocations}
                onSearchResultItemEnter={handleSearchResultItemEnter}
                onSearchResultItemLeave={handleSearchResultItemLeave}
                onSearchResultItemClicked={handleSearchResultItemClicked}
                searchString={searchString}                
                categories={categories}
                selectedLocation={selectedLocation}
                onSearchResultReset={handleSearchReset}
              />
            }
            {/* Search Result Event */}
            {searchResultType === SEARCH_RESULT_TYPE.EVENT &&
              <SearchResultEvent
                showResult={showResult}
                events={filteredEvents}
                onSearchResultItemEnter={handleSearchResultEventItemEnter}
                onSearchResultItemLeave={handleSearchResultEventItemLeave}
                onSearchResultItemClicked={handleSearchResultEventItemClicked}
                searchString={searchString}                
                categories={categories}
                selectedEvent={selectedEvent}
                onSearchResultReset={handleSearchReset}
              />
            }
          </div>
        </div>
      </div>

      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          zoom={defaultZoom}
          center={defaultCenter}
          onLoad={(map) => setMap(map)}
          options={{
            styles: MapStylesGreen,
            gestureHandling: 'cooperative',
            disableDefaultUI: true
          }}
        >
          {(searchResultType === SEARCH_RESULT_TYPE.DEFAULT || searchResultType === SEARCH_RESULT_TYPE.SHOWROOM) && filteredLocations.map((location, index) => (
            <CustomMapMarker
              key={`location-${location.id}`}
              location={location}
              onClick={handleMarkerClick}
              allowTooltipShown={showResult}
            />
          ))}

          {searchResultType === SEARCH_RESULT_TYPE.EVENT && filteredEvents.map((event, index) => (
            <EventMapMarker
              key={`event-${event.id}`}
              event={event}
              onClick={handleEventMarkerClick}
              allowTooltipShown={showResultEvent}
            />
          ))}
        </GoogleMap>
      ) : (
        <div>Loading map...</div>
      )}
    </div>
  );
};

export default App;
